import { UnifiedListLayout } from '@/components/layout/unified-list-layout';
import { IntroCard } from '@/components/shared/IntroCard';
import { UnifiedSearch } from '@/components/shared/UnifiedSearch';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { useProductMeta } from '@/hooks/meta/useProductMeta';
import { useProjectSettings } from '@/hooks/useProjectSettings';
import {
    useAddNoteToFolder,
    useCreateNote,
    useCreateNoteFolder,
    useDeleteNote,
    useDeleteNoteFolder,
    useFolderNotes,
    useFolderStructure,
    useUpdateNoteFolder
} from '@/services/query/notes-queries';
import { NoteFolder } from '@/services/supabase/notes';
import { formatDistanceToNow } from 'date-fns';
import { motion } from 'framer-motion';
import {
    Edit2,
    FilePlus,
    FileText,
    Folder,
    FolderPlus,
    LayoutGrid,
    MoreVertical,
    Trash2
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { findFolderInTree } from '@/services/supabase/notes';

export const NotesPage = () => {
    const { projectId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const currentFolderId = searchParams.get('folder') ? parseInt(searchParams.get('folder')!) : null;

    const navigate = useNavigate();
    const { toast } = useToast();

    // Settings
    const { useIntroDismissal } = useProjectSettings(projectId || '');
    const { isDismissed: isIntroDismissed, dismiss: dismissIntro } = useIntroDismissal('notes');

    // Queries
    const { data: folderStructure = [], isLoading: isLoadingFolders } = useFolderStructure(projectId);
    const { data: currentFolderNotes = [], isLoading: isLoadingNotes } = useFolderNotes(currentFolderId, projectId);
    const { tenant } = useProductMeta();

    // Mutations
    const createFolder = useCreateNoteFolder();
    const updateFolder = useUpdateNoteFolder();
    const deleteFolder = useDeleteNoteFolder();
    const createNote = useCreateNote();
    const addNoteToFolder = useAddNoteToFolder();
    const deleteNote = useDeleteNote();

    // State for Dialogs
    const [isNewFolderOpen, setIsNewFolderOpen] = useState(false);
    const [isRenameFolderOpen, setIsRenameFolderOpen] = useState(false);
    const [folderToRename, setFolderToRename] = useState<NoteFolder | null>(null);
    const [newFolderName, setNewFolderName] = useState('');
    const [searchQuery, setSearchQuery] = useState('');

    // Derived State
    const { currentFolderChildren, currentFolder } = useMemo(() => {
        if (currentFolderId) {
            const result = findFolderInTree(folderStructure, currentFolderId);
            if (result) {
                return {
                    currentFolder: result.folder,
                    currentFolderChildren: result.folder.children || []
                };
            }
        }
        // Root view
        return {
            currentFolder: null,
            currentFolderChildren: folderStructure.filter(f => !f.parent_folder_id) // Only root folders
        };
    }, [folderStructure, currentFolderId]);

    // Filtering
    const filteredFolders = currentFolderChildren.filter(f =>
        f.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredNotes = currentFolderNotes.filter(n =>
        (n.title || 'Untitled').toLowerCase().includes(searchQuery.toLowerCase())
    );

    // Handlers
    const handleCreateFolder = async () => {
        if (!newFolderName.trim() || !projectId || !tenant?.id) return;
        try {
            await createFolder.mutateAsync({
                name: newFolderName,
                parentFolderId: currentFolderId,
                projectId: projectId,
                tenantId: tenant.id
            });
            setIsNewFolderOpen(false);
            setNewFolderName('');
            toast({ title: 'Folder created' });
        } catch (e) {
            toast({ title: 'Failed to create folder', variant: 'destructive' });
        }
    };

    const handleRenameFolder = async () => {
        if (!folderToRename || !newFolderName.trim()) return;
        try {
            await updateFolder.mutateAsync({
                folderId: folderToRename.id,
                data: { name: newFolderName }
            });
            setIsRenameFolderOpen(false);
            setFolderToRename(null);
            setNewFolderName('');
            toast({ title: 'Folder renamed' });
        } catch (e) {
            toast({ title: 'Failed to rename folder', variant: 'destructive' });
        }
    };

    const handleDeleteFolder = async (id: number) => {
        try {
            if (confirm('Are you sure? This will delete the folder and its contents.')) {
                await deleteFolder.mutateAsync(id);
                toast({ title: 'Folder deleted' });
            }
        } catch (e) {
            toast({ title: 'Failed to delete folder', variant: 'destructive' });
        }
    };

    const handleCreateNote = async () => {
        if (!projectId) return;
        try {
            const newNote = await createNote.mutateAsync({
                title: 'Untitled Note',
                content: [],
                projectId: projectId
            });

            // If we are in a folder, add the note to it
            if (currentFolderId) {
                await addNoteToFolder.mutateAsync({
                    noteId: newNote.id,
                    folderId: currentFolderId,
                    projectId: projectId
                });
            }

            navigate(`/projects/${projectId}/notes/${newNote.id}`);
        } catch (e) {
            console.error(e);
            toast({ title: 'Failed to create note', variant: 'destructive' });
        }
    };

    return (
        <UnifiedListLayout
            className="p-8"
            toolbar={
                <div className="flex flex-col sm:flex-row items-center justify-between gap-4 w-full">
                    {/* Search - Left Side */}
                    <div className="flex items-center gap-4 w-full sm:w-auto flex-1">
                        <UnifiedSearch
                            placeholder="Search notes..."
                            value={searchQuery}
                            onChange={setSearchQuery}
                            className="w-full sm:w-[320px]"
                            shortcut={true}
                        />
                    </div>

                    {/* Actions - Right Side */}
                    <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                        <Button onClick={() => setIsNewFolderOpen(true)} variant="outline" size="sm" className="gap-2 h-9 rounded-full border-dashed text-foreground/80 hover:text-foreground">
                            <FolderPlus className="h-4 w-4" />
                            <span className="hidden lg:inline">New Folder</span>
                        </Button>
                        <Button onClick={handleCreateNote} size="sm" className="gap-2 h-9 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20">
                            <FilePlus className="h-4 w-4" />
                            <span className="hidden lg:inline">New Note</span>
                        </Button>
                    </div>
                </div>
            }
        >
            <div className="space-y-8 pb-20">
                {/* Intro Card */}
                {!isIntroDismissed && !isLoadingFolders && !isLoadingNotes && (filteredFolders.length === 0 && filteredNotes.length === 0) && (
                    <IntroCard
                        title="Research & Brainstorming"
                        description="Keep all your research, loose ideas, and world-building notes in one place. Organize them with folders to maintain a clean workspace for your creativity."
                        onClose={dismissIntro}
                        className="mb-0"
                        actions={
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground/80">Organization</h4>
                                    <p className="text-xs text-muted-foreground leading-relaxed">
                                        Use nested folders to build a wiki-like structure for your story bible.
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <Button
                                        variant="outline"
                                        className="w-full justify-start text-xs h-9"
                                        onClick={() => setIsNewFolderOpen(true)}
                                    >
                                        <FolderPlus className="h-3 w-3 mr-2" />
                                        Folder
                                    </Button>
                                    <Button
                                        variant="default"
                                        className="w-full justify-start text-xs h-9"
                                        onClick={handleCreateNote}
                                    >
                                        <FilePlus className="h-3 w-3 mr-2" />
                                        Note
                                    </Button>
                                </div>
                            </div>
                        }
                    />
                )}

                {/* Search Result Indicator */}
                {searchQuery && (
                    <p className="text-sm text-muted-foreground">
                        Searching for "{searchQuery}" in {currentFolder ? currentFolder.name : 'Root'}
                    </p>
                )}

                {/* Folders Section */}
                {filteredFolders.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="space-y-4"
                    >
                        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-2">
                            <LayoutGrid className="h-3 w-3" />
                            Folders
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {filteredFolders.map((folder) => (
                                <div
                                    key={folder.id}
                                    onClick={() => setSearchParams({ folder: folder.id.toString() })}
                                    className="group relative flex items-center justify-between p-3 bg-card hover:bg-accent/40 border border-border/50 hover:border-primary/30 rounded-lg transition-all duration-200 cursor-pointer backdrop-blur-sm"
                                >
                                    <div className="flex items-center gap-3 min-w-0 flex-1">
                                        <div className="p-2 bg-muted/40 rounded-lg text-foreground group-hover:scale-105 transition-transform duration-300 shrink-0">
                                            <Folder className="h-5 w-5 fill-muted-foreground/20 stroke-current text-muted-foreground" />
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <h3 className="font-medium text-sm truncate group-hover:text-primary transition-colors pr-2">{folder.name}</h3>
                                            <span className="text-[10px] text-muted-foreground truncate">
                                                {folder.children?.length || 0} items
                                            </span>
                                        </div>
                                    </div>

                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
                                                <MoreVertical className="h-4 w-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-40">
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                setFolderToRename(folder as NoteFolder);
                                                setNewFolderName(folder.name);
                                                setIsRenameFolderOpen(true);
                                            }}>
                                                <Edit2 className="mr-2 h-4 w-4" /> Rename
                                            </DropdownMenuItem>
                                            <DropdownMenuSeparator />
                                            <DropdownMenuItem
                                                className="text-destructive focus:text-destructive"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteFolder(folder.id);
                                                }}
                                            >
                                                <Trash2 className="mr-2 h-4 w-4" /> Delete
                                            </DropdownMenuItem>
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Files Section */}
                {filteredNotes.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 }}
                        className="space-y-4"
                    >
                        <h2 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center justify-between">
                            <span className="flex items-center gap-2">
                                <FileText className="h-3 w-3" />
                                Files
                            </span>
                            <span className="text-[10px] font-normal opacity-50 border border-border px-2 py-0.5 rounded-full">
                                {filteredNotes.length}
                            </span>
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                            {filteredNotes.map((note) => (
                                <div
                                    key={note.id}
                                    onClick={() => navigate(`/projects/${projectId}/notes/${note.id}`)}
                                    className="group relative flex flex-col bg-card hover:bg-accent/40 border border-border/50 hover:border-primary/30 rounded-xl transition-all duration-200 cursor-pointer overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5"
                                >
                                    <div className="aspect-[4/3] bg-muted/20 border-b border-border/30 group-hover:bg-muted/30 transition-colors p-4 relative overflow-hidden flex items-center justify-center">
                                        <div className="w-12 h-12 rounded-xl bg-background border border-border/50 flex items-center justify-center shadow-sm">
                                            <FileText className="h-6 w-6 text-muted-foreground/70" />
                                        </div>

                                        {/* Hover Overlay */}
                                        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-start justify-end p-2">
                                            <DropdownMenu>
                                                <DropdownMenuTrigger asChild>
                                                    <Button variant="secondary" size="icon" className="h-6 w-6 rounded-full shadow-lg bg-background/90 hover:bg-background">
                                                        <MoreVertical className="h-3 w-3" />
                                                    </Button>
                                                </DropdownMenuTrigger>
                                                <DropdownMenuContent align="end">
                                                    <DropdownMenuItem
                                                        className="text-destructive focus:text-destructive"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            if (confirm("Delete this note?")) deleteNote.mutate(note.id);
                                                        }}
                                                    >
                                                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <div className="flex items-center gap-2 mb-1">
                                            <h3 className="font-medium text-sm truncate flex-1 group-hover:text-primary transition-colors">
                                                {note.title || "Untitled Note"}
                                            </h3>
                                        </div>
                                        <div className="flex items-center justify-between text-[10px] text-muted-foreground/70">
                                            <span>
                                                {formatDistanceToNow(new Date(note.updated_at || note.created_at), { addSuffix: true })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}

                {/* Empty State */}
                {!isLoadingFolders && !isLoadingNotes && filteredFolders.length === 0 && filteredNotes.length === 0 && (
                    <div className="flex flex-col items-center justify-center py-32 text-muted-foreground animate-in zoom-in-95 duration-300">
                        <div className="h-24 w-24 bg-muted/20 rounded-3xl flex items-center justify-center mb-6 border border-border/50">
                            <Folder className="h-10 w-10 text-muted-foreground/30 fill-current" />
                        </div>
                        <p className="text-xl font-medium text-foreground">Empty Folder</p>
                        <p className="text-sm opacity-60 max-w-xs text-center mt-2 mb-8">
                            Get started by creating a new folder or note using the buttons above.
                        </p>
                        <div className="flex gap-3">
                            <Button onClick={() => setIsNewFolderOpen(true)} variant="outline" className="gap-2 rounded-full">
                                <FolderPlus className="h-4 w-4" />
                                Create Folder
                            </Button>
                            <Button onClick={handleCreateNote} className="gap-2 rounded-full shadow-lg shadow-primary/20">
                                <FilePlus className="h-4 w-4" />
                                Create Note
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Dialogs */}
            <Dialog open={isNewFolderOpen} onOpenChange={setIsNewFolderOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Create New Folder</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="folderRequest">Folder Name</Label>
                        <Input
                            id="folderRequest"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            placeholder="e.g. Character Sketches"
                            className="mt-2"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsNewFolderOpen(false)}>Cancel</Button>
                        <Button onClick={handleCreateFolder}>Create Folder</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isRenameFolderOpen} onOpenChange={setIsRenameFolderOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rename Folder</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <Label htmlFor="renameFolder">Folder Name</Label>
                        <Input
                            id="renameFolder"
                            value={newFolderName}
                            onChange={(e) => setNewFolderName(e.target.value)}
                            className="mt-2"
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && handleRenameFolder()}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsRenameFolderOpen(false)}>Cancel</Button>
                        <Button onClick={handleRenameFolder}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </UnifiedListLayout >
    );
};

export default NotesPage;
